/* ==========================================================================
   Rook UI Core — Tabs Controller
   Web Component: <rk-tabs-element>
   ========================================================================== */

if (!customElements.get('rk-tabs-element')) {
    class RkTabsElement extends HTMLElement {
        constructor() {
            super();
            this.tabs = [];
            this.panels = [];
            this.activeTabId = null;

            this._onKeyDown = this._onKeyDown.bind(this);
            this._onClick = this._onClick.bind(this);
        }

        connectedCallback() {
            // Find all tabs and panels within the element
            this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
            this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

            if (this.tabs.length === 0 || this.panels.length === 0) return;

            this._bindEvents();
            this._setupA11y();

            // Set initial state
            const defaultId = this.dataset.defaultValue;
            const tabToActivate = this.tabs.find((tab) => tab.id === defaultId) || this.tabs[0];

            if (tabToActivate) {
                this.activateTab(tabToActivate.id, false);
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Setup                                                             */
        /* ------------------------------------------------------------------ */

        _setupA11y() {
            // Ensure tablist has role
            const tabList = this.querySelector('.rk-tabs__list');
            if (tabList && !tabList.hasAttribute('role')) {
                tabList.setAttribute('role', 'tablist');
            }

            this.tabs.forEach((tab) => {
                tab.setAttribute('tabindex', '-1');
                tab.setAttribute('aria-selected', 'false');
            });

            this.panels.forEach((panel) => {
                panel.setAttribute('tabindex', '0');
                if (!panel.hasAttribute('hidden')) {
                    panel.setAttribute('hidden', 'true');
                }
            });
        }

        _bindEvents() {
            const tabList = this.querySelector('.rk-tabs__list');
            if (tabList) {
                tabList.addEventListener('keydown', this._onKeyDown);
                tabList.addEventListener('click', this._onClick);
            } else {
                this.tabs.forEach((tab) => {
                    tab.addEventListener('keydown', this._onKeyDown);
                    tab.addEventListener('click', this._onClick);
                });
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Interactions                                                      */
        /* ------------------------------------------------------------------ */

        _onClick(e) {
            const target = e.target.closest('[role="tab"]');
            if (!target || target.disabled || target.getAttribute('aria-disabled') === 'true') return;

            this.activateTab(target.id, true);
        }

        _onKeyDown(e) {
            const target = e.target.closest('[role="tab"]');
            if (!target) return;

            const currentIndex = this.tabs.indexOf(target);
            let nextIndex = currentIndex;

            const orientation = this.dataset.orientation || 'horizontal';

            switch (e.key) {
                case 'ArrowRight':
                    if (orientation === 'horizontal') nextIndex = currentIndex + 1;
                    break;
                case 'ArrowLeft':
                    if (orientation === 'horizontal') nextIndex = currentIndex - 1;
                    break;
                case 'ArrowDown':
                    if (orientation === 'vertical') nextIndex = currentIndex + 1;
                    break;
                case 'ArrowUp':
                    if (orientation === 'vertical') nextIndex = currentIndex - 1;
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'End':
                    nextIndex = this.tabs.length - 1;
                    break;
                default:
                    return; // Let other keys behave normally
            }

            e.preventDefault(); // Prevent page scroll for arrows

            // Wrap around
            if (nextIndex >= this.tabs.length) {
                nextIndex = 0;
            } else if (nextIndex < 0) {
                nextIndex = this.tabs.length - 1;
            }

            const nextTab = this.tabs[nextIndex];

            // Skip disabled tabs
            if (nextTab.disabled || nextTab.getAttribute('aria-disabled') === 'true') {
                return; // Simple approach: do not move if next is disabled. (Complex approach: loop until non-disabled).
            }

            nextTab.focus();

            // Auto-activate on focus in Shadcn/UI default behavior
            this.activateTab(nextTab.id, true);
        }

        /* ------------------------------------------------------------------ */
        /*  Public API                                                        */
        /* ------------------------------------------------------------------ */

        activateTab(tabId, focus = false) {
            if (this.activeTabId === tabId) return;

            this.tabs.forEach((tab) => {
                const isSelected = tab.id === tabId;
                tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
                tab.setAttribute('tabindex', isSelected ? '0' : '-1');

                if (isSelected && focus) {
                    tab.focus();
                }
            });

            this.panels.forEach((panel) => {
                const controlledBy = panel.getAttribute('aria-labelledby');
                if (controlledBy === tabId) {
                    panel.removeAttribute('hidden');
                    panel.classList.add('rk-tabs__panel--active');
                } else {
                    panel.setAttribute('hidden', 'true');
                    panel.classList.remove('rk-tabs__panel--active');
                }
            });

            this.activeTabId = tabId;

            this.dispatchEvent(
                new CustomEvent('rk:tabs:change', {
                    bubbles: true,
                    detail: { activeTabId: tabId },
                })
            );
        }
    }

    customElements.define('rk-tabs-element', RkTabsElement);
}
